<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';
import { NConfigProvider, NMessageProvider, NNotificationProvider } from 'naive-ui';
import { useUserStore } from '@/stores/user';
import { useAgentStore } from '@/stores/agent';
import { useConnectionStore } from '@/stores/connection';
import { connectWebSocket, disconnectWebSocket } from '@/services/websocket';
import { scheduleTokenRefresh, stopTokenRefresh } from '@/services/token';
import type { PageContext } from '@/types';
import AppLayout from '@/components/AppLayout.vue';

const userStore = useUserStore();
const agentStore = useAgentStore();
const connectionStore = useConnectionStore();

onMounted(async () => {
  const params = new URLSearchParams(window.location.search);
  const installId = params.get('X-install-id') || sessionStorage.getItem('X-install-id') || '';

  // Persist for page refreshes within the same session
  if (installId) {
    sessionStorage.setItem('X-install-id', installId);
  }

  if (!installId && !import.meta.env.DEV) {
    connectionStore.addAlert({
      type: 'token-expired',
      severity: 'error',
      message: 'No install ID provided. Unable to authenticate.',
      retryable: false,
    });
    return;
  }

  try {
    let ctx: PageContext;

    if (import.meta.env.DEV && !installId) {
      const mockResponse = await fetch(import.meta.env.BASE_URL + 'dev/mock-context.json');
      ctx = await mockResponse.json();
      console.debug('[DEV] Using mock PageContext');
    } else {
      const response = await fetch('/call-center/init', {
        headers: { 'X-install-id': installId },
      });

      if (!response.ok) {
        throw new Error(`Init failed: ${response.status}`);
      }

      ctx = await response.json();
    }

    userStore.initialize(ctx);
    await agentStore.initializeFromNs();

    if (ctx.jwt && !ctx.jwt.includes('mock')) {
      scheduleTokenRefresh();
      connectWebSocket();
    } else {
      console.debug('[DEV] Skipping WS connect and token refresh (mock JWT)');
    }
  } catch (err) {
    connectionStore.addAlert({
      type: 'api-error',
      severity: 'error',
      message: `Failed to initialize: ${err instanceof Error ? err.message : 'Unknown error'}`,
      retryable: true,
    });
  }
});

onUnmounted(() => {
  disconnectWebSocket();
  stopTokenRefresh();
});
</script>

<template>
  <NConfigProvider>
    <NMessageProvider>
      <NNotificationProvider>
        <AppLayout v-if="userStore.isAuthenticated" />
        <div v-else class="loading-container">
          <p>Loading...</p>
        </div>
      </NNotificationProvider>
    </NMessageProvider>
  </NConfigProvider>
</template>

<style>
* {
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  font-family: system-ui, sans-serif;
}
</style>
